import { useEffect, useState } from "react";
import { useAmbientSound } from "@/context/AmbientSoundContext";
import { Music2, Volume2, VolumeX, Check, ChevronUp, X, Briefcase, RotateCcw } from "lucide-react";

function formatRemaining(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

/**
 * Widget flottant CONFILEX — ambiance sonore premium.
 * Discret, doré, en bas à droite, jamais intrusif.
 */
export default function AmbientSoundWidget() {
  const {
    AMBIENCES,
    ambienceId,
    setAmbienceId,
    volume,
    setVolume,
    enabled,
    toggleEnabled,
    hasBeenInvited,
    setHasBeenInvited,
    startMeetingMute,
    cancelMeetingMute,
    meetingActive,
    meetingRemainingSec,
    meetingAuto,
    nowPlaying,
    playbackBlocked,
  } = useAmbientSound();
  const [panelOpen, setPanelOpen] = useState(false);
  const [showInvite, setShowInvite] = useState(false);

  // Affiche l'invitation 1ʳᵉ visite après 3,5s, une seule fois
  useEffect(() => {
    if (hasBeenInvited) return;
    const t = setTimeout(() => setShowInvite(true), 3500);
    return () => clearTimeout(t);
  }, [hasBeenInvited]);

  const onAcceptInvite = () => {
    setShowInvite(false);
    toggleEnabled(true);
  };

  const onDismissInvite = () => {
    setShowInvite(false);
    setHasBeenInvited(true);
  };

  return (
    <>
      {/* Invitation 1ʳᵉ visite */}
      {showInvite && !enabled && (
        <div
          data-testid="ambient-invite"
          className="fixed bottom-24 left-6 z-[60] max-w-[320px] rounded-2xl border border-[#C9A961]/40 bg-[#0B3A2A] text-white shadow-2xl shadow-black/30 p-4 animate-fadeUp"
          style={{ animation: "fadeUp 0.5s ease both" }}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-[#C9A961]/15 flex items-center justify-center">
                <Music2 className="w-4 h-4 text-[#C9A961]" />
              </div>
              <div>
                <p className="text-[13px] font-medium tracking-wide">Ambiance piano CONFILEX</p>
                <p className="text-[10px] text-[#C9A961] uppercase tracking-[0.18em]">Chopin · Debussy · Satie</p>
              </div>
            </div>
            <button
              onClick={onDismissInvite}
              className="text-white/40 hover:text-white/80 transition"
              data-testid="ambient-invite-dismiss"
              aria-label="Fermer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-white/65 leading-relaxed mt-3">
            Activez une sélection de nocturnes et de pièces néoclassiques en arrière-plan,
            pensée pour accompagner votre lecture. Réglable et désactivable à tout moment.
          </p>
          <div className="flex gap-2 mt-3">
            <button
              onClick={onAcceptInvite}
              data-testid="ambient-invite-accept"
              className="text-[12px] px-3 py-1.5 rounded-lg bg-[#C9A961] text-[#0B3A2A] font-medium hover:brightness-110 transition"
            >
              Activer le piano
            </button>
            <button
              onClick={onDismissInvite}
              data-testid="ambient-invite-later"
              className="text-[12px] px-3 py-1.5 rounded-lg border border-white/20 text-white/75 hover:bg-white/5 transition"
            >
              Plus tard
            </button>
          </div>
        </div>
      )}

      {/* Panneau de réglages */}
      {panelOpen && (
        <div
          data-testid="ambient-panel"
          className="fixed bottom-24 left-6 z-50 w-[320px] rounded-2xl border border-[#C9A961]/30 bg-white shadow-2xl shadow-black/20 overflow-hidden"
          style={{ animation: "fadeUp 0.35s ease both" }}
        >
          <div className="px-4 py-3 bg-gradient-to-br from-[#0B3A2A] to-[#0B3A2A] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-[#C9A961]/20 flex items-center justify-center">
                <Music2 className="w-3.5 h-3.5 text-[#C9A961]" />
              </div>
              <div>
                <p className="text-white text-[12px] uppercase tracking-[0.18em] font-semibold">Ambiance</p>
                <p className="text-white/55 text-[10px]">Piano feutré · Néoclassique</p>
              </div>
            </div>
            <button
              onClick={() => setPanelOpen(false)}
              className="text-white/50 hover:text-white transition"
              aria-label="Fermer le panneau"
              data-testid="ambient-panel-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="p-4 space-y-4">
            {/* Bandeau "audio bloqué" — guidance utilisateur */}
            {playbackBlocked && enabled && (
              <div data-testid="ambient-blocked-banner"
                className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-[12px] text-amber-900 leading-relaxed">
                <p className="font-semibold mb-1">Le navigateur a bloqué le son.</p>
                <p>
                  Cliquez à nouveau sur le bouton ci-dessous, ou vérifiez que l'onglet n'est pas
                  muté (clic droit sur l'onglet → « Réactiver le son du site »).
                </p>
              </div>
            )}

            {/* Now playing — visible uniquement quand l'ambiance joue */}
            {enabled && nowPlaying && nowPlaying.title && !meetingActive && (
              <div data-testid="ambient-now-playing"
                className="rounded-lg border border-[#C9A961]/20 bg-gradient-to-br from-[#FAF7F1] to-white px-3 py-2.5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-[#C9A961] mb-0.5 flex items-center gap-1.5">
                      <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-[#C9A961]">
                        <span className="absolute inset-0 rounded-full bg-[#C9A961] animate-ping" />
                      </span>
                      En cours
                    </p>
                    <p className="text-[13px] text-[#0B3A2A] font-medium truncate">
                      {nowPlaying.title}
                    </p>
                    <p className="text-[11px] text-slate-500 italic truncate">
                      {nowPlaying.composer}
                    </p>
                  </div>
                  {nowPlaying.duration > 0 && (
                    <span className="text-[10px] text-slate-400 tabular-nums shrink-0 mt-1">
                      {Math.floor(nowPlaying.currentTime / 60)}:{String(Math.floor(nowPlaying.currentTime % 60)).padStart(2, "0")}
                      {" / "}
                      {Math.floor(nowPlaying.duration / 60)}:{String(Math.floor(nowPlaying.duration % 60)).padStart(2, "0")}
                    </span>
                  )}
                </div>
                {/* Micro progress bar */}
                <div className="mt-2 h-[2px] bg-[#0B3A2A]/8 rounded overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[#C9A961] to-[#E3C77A] transition-[width] duration-700"
                    style={{ width: `${(nowPlaying.progress || 0) * 100}%` }}
                  />
                </div>
              </div>
            )}

            {/* Toggle on/off */}
            <div className="flex items-center justify-between">
              <span className="text-[12px] text-slate-600">État</span>
              <button
                onClick={() => toggleEnabled()}
                data-testid="ambient-toggle"
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                  enabled ? "bg-[#C9A961]" : "bg-slate-300"
                }`}
                aria-pressed={enabled}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                    enabled ? "translate-x-5" : "translate-x-0.5"
                  }`}
                />
              </button>
            </div>

            {/* Sélecteur d'ambiance */}
            <div>
              <p className="text-[11px] uppercase tracking-[0.12em] text-slate-500 mb-2">Sélection</p>
              <div className="space-y-1.5 max-h-[280px] overflow-y-auto pr-1">
                {AMBIENCES.map((a) => {
                  const active = a.id === ambienceId;
                  return (
                    <button
                      key={a.id}
                      onClick={() => setAmbienceId(a.id)}
                      data-testid={`ambient-choice-${a.id}`}
                      className={`w-full text-left px-3 py-2 rounded-lg border transition flex items-start justify-between gap-3 ${
                        active
                          ? "border-[#C9A961] bg-[#C9A961]/8"
                          : "border-slate-200 hover:border-[#C9A961]/40 hover:bg-slate-50"
                      }`}
                    >
                      <div className="min-w-0">
                        <p className="text-[13px] font-medium text-[#0B3A2A] truncate">{a.label}</p>
                        <p className="text-[11px] text-slate-500 truncate">{a.description}</p>
                        {a.inspiration && (
                          <p className="text-[10px] text-[#C9A961]/80 italic truncate mt-0.5">
                            Inspiré · {a.inspiration}
                          </p>
                        )}
                      </div>
                      {active && <Check className="w-4 h-4 text-[#C9A961] shrink-0 mt-0.5" />}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Mode Réunion en cours */}
            <div data-testid="ambient-meeting-block">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[11px] uppercase tracking-[0.12em] text-slate-500 flex items-center gap-1.5">
                  <Briefcase className="w-3 h-3" /> Réunion en cours
                </p>
                {meetingActive && (
                  <span className="text-[11px] tabular-nums text-[#8B3A3A] font-medium" data-testid="ambient-meeting-countdown">
                    {formatRemaining(meetingRemainingSec)}
                  </span>
                )}
              </div>
              {meetingActive ? (
                <div className="rounded-lg border border-[#8B3A3A]/25 bg-[#8B3A3A]/5 px-3 py-2.5 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[12px] text-[#0B3A2A] font-medium">
                      Ambiance suspendue
                      {meetingAuto && (
                        <span className="ml-1.5 inline-block text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#8B3A3A]/15 text-[#8B3A3A] font-semibold">
                          Détecté
                        </span>
                      )}
                    </p>
                    <p className="text-[11px] text-slate-500">
                      {meetingAuto ? "Visioconférence en cours — reprise auto." : "Reprise automatique au terme."}
                    </p>
                  </div>
                  <button
                    onClick={cancelMeetingMute}
                    data-testid="ambient-meeting-cancel"
                    className="text-[11px] inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 transition"
                    aria-label="Reprendre immédiatement"
                  >
                    <RotateCcw className="w-3 h-3" /> Reprendre
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-1.5">
                  {[15, 30, 60].map((m) => (
                    <button
                      key={m}
                      onClick={() => startMeetingMute(m)}
                      data-testid={`ambient-meeting-${m}`}
                      className="text-[12px] px-2 py-2 rounded-lg border border-slate-200 text-[#0B3A2A] hover:border-[#C9A961]/50 hover:bg-[#C9A961]/5 transition font-medium"
                    >
                      {m} min
                    </button>
                  ))}
                </div>
              )}
              <p className="text-[10px] text-slate-400 mt-1.5">
                Coupe l'ambiance temporairement — détection automatique si une visioconférence démarre.
              </p>
            </div>

            {/* Volume */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-[11px] uppercase tracking-[0.12em] text-slate-500">Volume</p>
                <span className="text-[11px] text-slate-400 tabular-nums">{Math.round(volume * 100)} %</span>
              </div>
              <div className="flex items-center gap-2">
                <VolumeX className="w-3.5 h-3.5 text-slate-400" />
                <input
                  data-testid="ambient-volume"
                  type="range"
                  min={0}
                  max={70}
                  value={Math.round(volume * 100)}
                  onChange={(e) => setVolume(Number(e.target.value) / 100)}
                  className="flex-1 accent-[#C9A961]"
                  aria-label="Volume"
                />
                <Volume2 className="w-3.5 h-3.5 text-slate-400" />
              </div>
              <p className="text-[10px] text-slate-400 mt-1">Volume conservateur — l'ambiance doit rester en arrière-plan.</p>
            </div>

            <p className="text-[10px] text-slate-400 leading-relaxed pt-1 border-t border-slate-100">
              Mélodies courtes en rotation (10–15s) — sons synthétisés en temps réel, aucune donnée externe.
              Pause automatique en visioconférence ou onglet inactif. Variation subtile selon l'heure du jour.
            </p>
          </div>
        </div>
      )}

      {/* Bouton flottant principal */}
      <button
        onClick={() => setPanelOpen((s) => !s)}
        data-testid="ambient-fab"
        aria-label="Ambiance sonore"
        className={`fixed bottom-6 left-6 z-50 group flex items-center gap-2 rounded-full pl-3 pr-4 py-2.5 border shadow-xl shadow-black/20 transition-all duration-300 ${
          meetingActive
            ? "bg-white border-[#8B3A3A]/40 text-[#8B3A3A]"
            : enabled
            ? "bg-[#0B3A2A] border-[#C9A961]/50 text-white hover:brightness-110"
            : "bg-[#0B3A2A] border-[#C9A961]/60 text-white hover:brightness-110"
        }`}
        style={{ animation: !hasBeenInvited ? "ambientPulse 2.6s ease-in-out infinite" : "none" }}
      >
        <span className={`relative inline-flex items-center justify-center w-7 h-7 rounded-full ${
          meetingActive ? "bg-[#8B3A3A]/10" : "bg-[#C9A961]/20"
        }`}>
          {meetingActive ? (
            <Briefcase className="w-3.5 h-3.5 text-[#8B3A3A]" />
          ) : (
            <Music2 className="w-3.5 h-3.5 text-[#C9A961]" />
          )}
          {enabled && !meetingActive && (
            <span className="absolute inset-0 rounded-full border border-[#C9A961]/40 animate-ping" />
          )}
        </span>
        <span className="text-[11px] tracking-[0.18em] uppercase font-medium hidden sm:inline">
          {meetingActive
            ? `Réunion · ${formatRemaining(meetingRemainingSec)}`
            : enabled
            ? "Ambiance"
            : "Écouter le piano"}
        </span>
        <ChevronUp className={`w-3.5 h-3.5 transition-transform ${panelOpen ? "rotate-180" : ""}`} />
      </button>

      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes ambientPulse {
          0%, 100% { box-shadow: 0 12px 30px -8px rgba(196,164,104,0.0), 0 0 0 0 rgba(196,164,104,0.4); }
          50% { box-shadow: 0 12px 30px -8px rgba(196,164,104,0.35), 0 0 0 6px rgba(196,164,104,0); }
        }
      `}</style>
    </>
  );
}
